import { Autocomplete } from "@mui/material";
import BootstrapTextField from "Common/BootstrapTextField";
import Colors from "Constants/Color";
import { autocompleteDropdownStyles, tableStyles } from "Constants/Style";
import { Axios } from "api/axios";
import ActionButton from "components/Buttons/ActionButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import { CloseButton } from "components/Buttons/CloseButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import PageContainer from "components/PageContainer/PageContainer";
import OrderStatusBadge from "components/StatusBadge/OrderStatusBadge";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import { useFormik } from "formik";
import { api } from "helpers/api_url";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Form, Row, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";
import Swal from "sweetalert2";
import { showConfirm } from "utils/swalHelper";
import * as Yup from "yup";
import ViewBagsModal from "./ViewBagsModal";
import { useAutoMinimizeSidebar } from "CustomHooks/useAutoMinimizeSidebar";

interface DispatchFormValues {
  dispatch_date_and_time: string;
  order_no: string;
  // invoice_no: string;
  issue_from: any;
  upload_document: File | null;
  existing_document_file: string | null; // Store existing document URL for edit mode
  remark: string;
}

const WarehouseReadyForDispatchForm: React.FC = () => {
  const navigate = useNavigate();
  const { order_id } = useParams<{ order_id?: string }>();
  const dispatch = useDispatch<any>();
  const [data, setData] = useState<any>({ order_items: [] });
  const [viewBagsForItem, setViewBagsForItem] = useState<any[]>([]);
  const [show, setShow] = useState<boolean>(false);
  const [currentItemIndex, setCurrentItemIndex] = useState<number | null>(null);
  const { loading: grnLoading } = useSelector((state: any) => state.grn);
  const isEdit = false;
  // Check if we're in edit mode (ID in URL params)

  const [dispatch_items, set_dispatch_items] = useState<any[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
  // Track verified items - key is item index, value is array of verified bag indices
  const [verifiedItems, setVerifiedItems] = useState<Record<number, number[]>>(
    {}
  );

  // Auto-minimize sidebar on tablet screens
  useAutoMinimizeSidebar();

  // Calculate dispatch quantity for each item based on verified bags
  useEffect(() => {
    set_dispatch_items((prevItems) => {
      return prevItems.map((item: any, idx: number) => {
        const verifiedBags = verifiedItems[idx] || [];
        let dispatchQuantity = 0;

        if (item?.bags && Array.isArray(item.bags) && item.bags.length > 0) {
          if (verifiedBags.length > 0) {
            dispatchQuantity = verifiedBags.reduce(
              (sum: number, bagIdx: number) => {
                const bag = item.bags[bagIdx];
                return sum + (Number(bag?.issue_quantity) || 0);
              },
              0
            );
          }
        }

        return {
          ...item,
          dispatch_quantity: dispatchQuantity,
        };
      });
    });
  }, [verifiedItems]);

  // Calculate financial summary based on dispatched items only
  const dispatchFinancialSummary = useMemo(() => {
    // Filter items with dispatch_quantity > 0
    const dispatchedItems = dispatch_items.filter(
      (item: any) =>
        item?.dispatch_quantity && Number(item.dispatch_quantity) > 0
    );

    let subtotalBeforeDiscount = 0;
    let totalItemDiscounts = 0;
    let totalGST = 0;

    dispatchedItems.forEach((item: any) => {
      const rate = Number(item?.rate) || 0;
      const dispatchQuantity = Number(item?.dispatch_quantity) || 0;

      // Calculate amount for dispatched quantity
      const amount = rate * dispatchQuantity;
      subtotalBeforeDiscount += amount;

      // Calculate discount amount based on dispatch quantity
      const discount = Number(item?.discount) || 0;
      const discountType = item?.discount_type || "fixed";
      let discountAmount = 0;

      if (discount > 0) {
        if (discountType === "percentage") {
          // Calculate discount as percentage of amount
          discountAmount = (amount * discount) / 100;
        } else {
          // For fixed discount, calculate proportionally based on dispatch quantity
          const originalQuantity = Number(item?.quantity) || 1;
          const originalDiscountAmount = Number(item?.discount_amount) || 0;
          if (originalQuantity > 0) {
            discountAmount =
              (originalDiscountAmount / originalQuantity) * dispatchQuantity;
          }
        }
      }
      totalItemDiscounts += discountAmount;

      // Calculate net taxable value (after discount)
      const netTaxableValue = amount - discountAmount;

      // Calculate GST amount
      const gstPercent = Number(item?.gst?.gst_percent) || 0;
      const gstAmount = (netTaxableValue * gstPercent) / 100;
      totalGST += gstAmount;
    });

    const netTaxableValue = subtotalBeforeDiscount - totalItemDiscounts;
    const totalAmount = netTaxableValue + totalGST;

    return {
      subtotalBeforeDiscount,
      totalItemDiscounts,
      netTaxableValue,
      totalGST,
      totalAmount,
    };
  }, [dispatch_items]);

  const validationSchema = Yup.object({
    dispatch_date_and_time: Yup.string().required(
      "Please select dispatch date"
    ),
    order_no: Yup.string().required("Please enter order No."),
    // invoice_no: Yup.string().required("Please enter invoice No."),
    issue_from: Yup.object().nullable().required("Please select warehouse"),
    upload_document: Yup.mixed().nullable(),
    remark: Yup.string(),
  });

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const formik = useFormik<DispatchFormValues>({
    initialValues: {
      dispatch_date_and_time: getTodayDate(), // Auto-select today's date in add mode
      order_no: "",
      // invoice_no: "",
      issue_from: null,
      upload_document: null as File | null,
      existing_document_file: null as string | null,
      remark: "",
    },

    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Validate that at least one item has dispatch quantity > 0
      const hasDispatchQuantity = dispatch_items.some(
        (item: any) =>
          item?.dispatch_quantity && Number(item.dispatch_quantity) > 0
      );

      if (!hasDispatchQuantity) {
        toast.error("At least one item must be verified before dispatch.");
        return;
      }

      const formData = new FormData();
      // formData.append("dispatch_date_and_time", values.dispatch_date_and_time);
      formData.append("dispatch_date", values.dispatch_date_and_time);
      formData.append("order_no", values.order_no);
      // formData.append("invoice_no", values.invoice_no);
      formData.append(
        "issue_from_warehouse_id",
        values.issue_from?.warehouse_id || ""
      );
      formData.append("remark", values.remark || "");

      // Append file if it exists (must be File object, not string)
      if (values.upload_document && values.upload_document instanceof File) {
        formData.append("document_file", values.upload_document);
      }

      formData.append(
        "customer_details",
        JSON.stringify(data?.customer_details)
      );

      formData.append(
        "subtotal_before_total",
        Number(dispatchFinancialSummary.subtotalBeforeDiscount)?.toFixed(2)
      );
      formData.append(
        "total_item_discounts",
        Number(dispatchFinancialSummary.totalItemDiscounts)?.toFixed(2)
      );
      formData.append(
        "net_taxable_value",
        Number(dispatchFinancialSummary.netTaxableValue)?.toFixed(2)
      );
      formData.append(
        "total_gst",
        Number(dispatchFinancialSummary.totalGST)?.toFixed(2)
      );
      formData.append(
        "total_amount",
        Number(dispatchFinancialSummary.totalAmount)?.toFixed(2)
      );

      formData.append("dispatch_items", JSON.stringify(dispatch_items));

      console.log("formData : ", formData);
      // return;
      try {
        // Create GRN
        await Axios.post(
          `${api?.warehouse?.dispatch?.ready_for_dispatch?.create}/${data?.order_id}/create-dispatch`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        toast.success("Dispatch created successfully");

        navigate("/warehouse-dispatch");
      } catch (err: any) {
        handleSubmitError(err);
      }
    },
  });

  useEffect(() => {
    const fetchWarehouseOptions = async () => {
      try {
        const response = await dispatch(fetchWarehouseDropdown()).unwrap();
        setWarehouseOptions(response?.data || []);
      } catch (error) {
        console.error("Error fetching warehouse options:", error);
      }
    };
    fetchWarehouseOptions();
  }, [dispatch]);

  // Helper function to handle submit errors
  const handleSubmitError = useCallback((err: any) => {
    const responseData = err?.response?.data || err;
    const validationErrors = responseData?.data?.errors || responseData?.errors;

    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      try {
        Swal.fire({
          icon: "error",
          title:
            validationErrors.length > 1
              ? "Validation Errors"
              : "Validation Error",
          html: `<div style="text-align: left;">${validationErrors
            .map((err: string) => `• ${err}`)
            .join("<br>")}</div>`,
          confirmButtonText: "OK",
          allowOutsideClick: false,
          allowEscapeKey: true,
        });
      } catch (swalError) {
        toast.error(validationErrors.join("; "));
      }
    } else {
      const errorMessage =
        responseData?.error ||
        responseData?.message ||
        "Failed to process GRN. Please try again.";
      toast.error(errorMessage);
    }
  }, []);

  useEffect(() => {
    const fetctOrderData = async () => {
      setIsLoadingInitialData(true);
      try {
        const response = await Axios.get(
          `${api?.warehouse?.dispatch?.ready_for_dispatch?.ready_for_dispatch_by_id}/${order_id}`
        );
        const orderData = response?.data?.data;
        if (response?.data?.data) {
          setData(orderData || {});
          set_dispatch_items(
            orderData?.order_items?.map((item: any) => ({
              ...item,
              dispatch_quantity: 0,
            })) || []
          );
          formik.setFieldValue("order_no", orderData.order_no || "");
          formik.setFieldValue("issue_from", orderData.warehouse || "");

          setInitialDataLoaded(true);
        }
      } catch (error: any) {
        console.error("Error fetching Order data:", error);
        toast.error(error?.message || "Failed to load Order data");
        navigate("/warehouse-dispatch");
      } finally {
        setIsLoadingInitialData(false);
      }
    };

    fetctOrderData();
  }, []);

  return (
    <>
      <PageContainer>
        <FormHeader
          title={isEdit ? "Edit Dispatch" : "Create Dispatch"}
          firstLabel="Dispatch List"
          secondLabel={isEdit ? "Edit Dispatch" : "Create Dispatch"}
          handleArrowClick={() => navigate("/warehouse-dispatch")}
        />

        {isLoadingInitialData ? (
          <LoadingSpinner label="Loading Order data..." />
        ) : (
          <Form autoComplete="off" onSubmit={formik.handleSubmit}>
            <Row>
              <Col lg={3}>
                <div className="mb-3">
                  <Form.Label>
                    Dispatch Date <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="dispatch_date_and_time"
                    value={formik.values.dispatch_date_and_time}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.dispatch_date_and_time &&
                      formik.touched.dispatch_date_and_time
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.dispatch_date_and_time}
                  </Form.Control.Feedback>
                </div>
              </Col>

              <Col lg={3}>
                <div className="mb-3">
                  <Form.Label>
                    Order No. <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    disabled
                    name="order_no"
                    placeholder="Enter Order No."
                    value={formik.values.order_no}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.order_no && formik.touched.order_no
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.order_no}
                  </Form.Control.Feedback>
                </div>
              </Col>
              {/* <Col lg={3}>
                                <div className="mb-3">
                                    <Form.Label>
                                        Invoice No. <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="invoice_no"
                                        placeholder="Enter Invoice No."
                                        value={formik.values.invoice_no}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={
                                            !!formik.errors.invoice_no && formik.touched.invoice_no
                                        }
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.invoice_no}
                                    </Form.Control.Feedback>
                                </div>
                            </Col> */}

              <Col lg={3}>
                <div className="mb-3">
                  <Form.Label>
                    Issue From <span className="text-danger">*</span>
                  </Form.Label>

                  <Autocomplete
                    options={warehouseOptions}
                    disabled
                    getOptionLabel={(option: any) =>
                      option?.warehouse_name || ""
                    }
                    value={formik.values.issue_from}
                    onChange={(event, val: any) => {
                      formik.setFieldValue("issue_from", val || null);
                      formik.setFieldTouched("issue_from", true);
                    }}
                    onBlur={() => formik.setFieldTouched("issue_from", true)}
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select Warehouse"
                        name="issue_from"
                        error={
                          formik.touched.issue_from &&
                          Boolean(formik.errors.issue_from)
                        }
                        helperText={
                          typeof formik.errors.issue_from === "string" &&
                          formik.touched.issue_from &&
                          formik.errors.issue_from
                        }
                        fullWidth
                        size="small"
                        sx={{
                          background: "#FFF",
                          ...autocompleteDropdownStyles.inputSx,
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "#ced4da",
                            },
                            "&:hover fieldset": {
                              borderColor: "#ced4da",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#ced4da",
                            },
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt: any) => opt?.warehouse_name || ""
                      )
                    }
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#ced4da",
                        },
                        "&:hover fieldset": {
                          borderColor: "#ced4da",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#ced4da",
                        },
                      },
                    }}
                  />
                </div>
              </Col>

              {/* <Col lg={3}>
                                <div className="mb-3">
                                    <Form.Label>Remark</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="remark"
                                        placeholder="Enter Remark"
                                        value={formik.values.remark}
                                        onChange={formik.handleChange}
                                    />
                                </div>
                            </Col> */}

              <Col lg={3}>
                <div className="mb-3">
                  <Form.Label>
                    Upload document
                    {/* <span className="text-danger">*</span> */}
                  </Form.Label>

                  {/* Custom styled box */}
                  <div
                    className={`d-flex align-items-center justify-content-between p-2 border rounded ${
                      formik.errors.upload_document &&
                      formik.touched.upload_document
                        ? "border-danger"
                        : ""
                    }`}
                    style={{
                      height: "40px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      backgroundColor: "#fff",
                      borderColor:
                        formik.errors.upload_document &&
                        formik.touched.upload_document
                          ? "#dc3545"
                          : "#ced4da",
                    }}
                    onMouseEnter={(e) => {
                      if (
                        !formik.errors.upload_document ||
                        !formik.touched.upload_document
                      ) {
                        e.currentTarget.style.borderColor = "#80bdff";
                        e.currentTarget.style.backgroundColor = "#f8f9fa";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (
                        !formik.errors.upload_document ||
                        !formik.touched.upload_document
                      ) {
                        e.currentTarget.style.borderColor = "#ced4da";
                        e.currentTarget.style.backgroundColor = "#fff";
                      }
                    }}
                    onClick={() => {
                      document.getElementById("uploadDocInput")?.click();
                    }}
                  >
                    <span
                      className="text-muted"
                      style={{
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        marginRight: "8px",
                        minWidth: 0,
                      }}
                      title={
                        formik.values.upload_document instanceof File
                          ? formik.values.upload_document.name
                          : "Click to upload document"
                      }
                    >
                      {formik.values.upload_document instanceof File
                        ? formik.values.upload_document.name
                        : "Click to upload document"}
                    </span>

                    {/* Action buttons */}
                    <div
                      className="d-flex align-items-center gap-2"
                      onClick={(e) => e.stopPropagation()}
                      style={{ flexShrink: 0 }}
                    >
                      {formik.values.upload_document instanceof File ? (
                        <>
                          <i
                            className="bi bi-upload text-primary"
                            style={{
                              cursor: "pointer",
                              fontSize: "1rem",
                              transition: "transform 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              document
                                .getElementById("uploadDocInput")
                                ?.click();
                            }}
                            title="Change document"
                          ></i>
                          <i
                            className="bi bi-x-circle text-danger"
                            style={{
                              cursor: "pointer",
                              transition: "transform 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              formik.setFieldValue("upload_document", null);
                              // Reset file input
                              const fileInput = document.getElementById(
                                "uploadDocInput"
                              ) as HTMLInputElement;
                              if (fileInput) {
                                fileInput.value = "";
                              }
                            }}
                            title="Remove document"
                          ></i>
                        </>
                      ) : (
                        <i
                          className="bi bi-upload"
                          style={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            color: "#6c757d",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = "#007bff";
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = "#6c757d";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                        ></i>
                      )}
                    </div>
                  </div>

                  {/* Hidden real file input */}
                  <Form.Control
                    id="uploadDocInput"
                    type="file"
                    name="upload_document"
                    style={{ display: "none" }}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const file = event.currentTarget.files?.[0] || null;
                      formik.setFieldValue("upload_document", file);
                      // Clear existing_document_file when a new file is uploaded
                      if (file) {
                        formik.setFieldValue("existing_document_file", null);
                      }
                      formik.setFieldTouched("upload_document", true);
                    }}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.upload_document &&
                      formik.touched.upload_document
                    }
                  />

                  <Form.Control.Feedback type="invalid">
                    {formik.errors.upload_document}
                  </Form.Control.Feedback>
                </div>
              </Col>
            </Row>

            <hr />
            <div className="mb-3">
              <h6
                className="mb-3"
                style={{ fontWeight: "600", color: "#212529" }}
              >
                Customer Details
              </h6>
              <div
                style={{
                  backgroundColor: Colors?.background_color,
                  padding: "0 15px",
                  borderRadius: "8px",
                  border: `1px solid ${Colors?.background_color}`,
                }}
              >
                <Row className="mb-0 mt-3">
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>Customer Name </Form.Label>
                      <Form.Control
                        type="text"
                        name="remark"
                        placeholder="Enter Customer Name"
                        value={data?.customer_details?.customer_name || "-"}
                        // onChange={formik.handleChange}
                        style={{
                          background: "#FFF",
                          borderRadius: "3px",
                          border: "1px solid #ced4da",
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "13px",
                          padding: "6px 10px", // matches MUI small input
                          height: "38px", // matches MUI size="small"
                          boxShadow: "none",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#ced4da")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#ced4da")}
                      />
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>Customer Name for Transport </Form.Label>
                      <Form.Control
                        type="text"
                        name="remark"
                        placeholder="Enter Customer Name for Transport"
                        // value={formik.values.remark}
                        // onChange={formik.handleChange}
                        value={
                          data?.customer_details?.customer_name_for_transport ||
                          "-"
                        }
                        style={{
                          background: "#FFF",
                          borderRadius: "3px",
                          border: "1px solid #ced4da",
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "13px",
                          padding: "6px 10px", // matches MUI small input
                          height: "38px", // matches MUI size="small"
                          boxShadow: "none",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#ced4da")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#ced4da")}
                      />
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>Transporter Name </Form.Label>
                      <Form.Control
                        type="text"
                        name="remark"
                        placeholder="Enter Transporter Name"
                        // value={formik.values.remark}
                        // onChange={formik.handleChange}
                        value={data?.customer_details?.transporter_name || "-"}
                        style={{
                          background: "#FFF",
                          borderRadius: "3px",
                          border: "1px solid #ced4da",
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "13px",
                          padding: "6px 10px", // matches MUI small input
                          height: "38px", // matches MUI size="small"
                          boxShadow: "none",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#ced4da")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#ced4da")}
                      />
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>Contact Person Name </Form.Label>
                      <Form.Control
                        type="text"
                        name="remark"
                        placeholder="Enter Contact Person Name"
                        // value={formik.values.remark}
                        // onChange={formik.handleChange}
                        value={
                          data?.customer_details?.contact_person_name || "-"
                        }
                        style={{
                          background: "#FFF",
                          borderRadius: "3px",
                          border: "1px solid #ced4da",
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "13px",
                          padding: "6px 10px", // matches MUI small input
                          height: "38px", // matches MUI size="small"
                          boxShadow: "none",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#ced4da")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#ced4da")}
                      />
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>Contact Number </Form.Label>
                      <Form.Control
                        type="text"
                        name="remark"
                        placeholder="Enter Contact Number"
                        // value={formik.values.remark}
                        // onChange={formik.handleChange}
                        value={data?.customer_details?.contact_number || "-"}
                        style={{
                          background: "#FFF",
                          borderRadius: "3px",
                          border: "1px solid #ced4da",
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "13px",
                          padding: "6px 10px", // matches MUI small input
                          height: "38px", // matches MUI size="small"
                          boxShadow: "none",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#ced4da")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#ced4da")}
                      />
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>Address </Form.Label>
                      <Form.Control
                        type="text"
                        name="remark"
                        placeholder="Enter Address"
                        // value={formik.values.remark}
                        // onChange={formik.handleChange}
                        value={data?.customer_details?.address || "-"}
                        style={{
                          background: "#FFF",
                          borderRadius: "3px",
                          border: "1px solid #ced4da",
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "13px",
                          padding: "6px 10px", // matches MUI small input
                          height: "38px", // matches MUI size="small"
                          boxShadow: "none",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#ced4da")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#ced4da")}
                      />
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>City </Form.Label>
                      <Form.Control
                        type="text"
                        name="remark"
                        placeholder="Enter City"
                        // value={formik.values.remark}
                        // onChange={formik.handleChange}
                        value={data?.customer_details?.city || "-"}
                        style={{
                          background: "#FFF",
                          borderRadius: "3px",
                          border: "1px solid #ced4da",
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "13px",
                          padding: "6px 10px", // matches MUI small input
                          height: "38px", // matches MUI size="small"
                          boxShadow: "none",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#ced4da")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#ced4da")}
                      />
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>State </Form.Label>
                      <Form.Control
                        type="text"
                        name="remark"
                        placeholder="Enter State"
                        // value={formik.values.remark}
                        // onChange={formik.handleChange}
                        value={data?.customer_details?.state || "-"}
                        style={{
                          background: "#FFF",
                          borderRadius: "3px",
                          border: "1px solid #ced4da",
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "13px",
                          padding: "6px 10px", // matches MUI small input
                          height: "38px", // matches MUI size="small"
                          boxShadow: "none",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#ced4da")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#ced4da")}
                      />
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>Country </Form.Label>
                      <Form.Control
                        type="text"
                        name="remark"
                        placeholder="Enter Country"
                        // value={formik.values.remark}
                        // onChange={formik.handleChange}
                        value={data?.customer_details?.country || "-"}
                        style={{
                          background: "#FFF",
                          borderRadius: "3px",
                          border: "1px solid #ced4da",
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "13px",
                          padding: "6px 10px", // matches MUI small input
                          height: "38px", // matches MUI size="small"
                          boxShadow: "none",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#ced4da")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#ced4da")}
                      />
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>Pincode </Form.Label>
                      <Form.Control
                        type="text"
                        name="remark"
                        placeholder="Enter Pincode"
                        // value={formik.values.remark}
                        // onChange={formik.handleChange}
                        value={data?.customer_details?.pincode || "-"}
                        style={{
                          background: "#FFF",
                          borderRadius: "3px",
                          border: "1px solid #ced4da",
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "13px",
                          padding: "6px 10px", // matches MUI small input
                          height: "38px", // matches MUI size="small"
                          boxShadow: "none",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#ced4da")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#ced4da")}
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
            <hr />
            <div className="mb-3">
              <h6
                className="mb-3"
                style={{ fontWeight: "600", color: "#212529" }}
              >
                Item Details
              </h6>
              <div className="mb-2">
                <Table hover responsive className="align-middle text-center">
                  <thead>
                    <tr>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "80px" }}
                      >
                        Sr No.
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Category
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Sub Category
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Item Name
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Item Code
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Unit
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Order Quantity
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Dispatch Quantity
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Rate
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Amount
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Discount
                      </th>
                      {/* <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>Discount Type</th> */}
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Discount Amount
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        GST%
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        GST Amount
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Total Amount
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Status
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Remarks
                      </th>
                      <th
                        style={{
                          ...tableStyles.headerAlt,
                          ...tableStyles.actionHeader,
                          minWidth: "100px",
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Existing Rows */}
                    {dispatch_items?.length === 0 ? (
                      <tr>
                        <td
                          colSpan={18}
                          style={{ ...tableStyles.cell, textAlign: "center" }}
                          className="py-3"
                        >
                          No Items added. Please add at least one item.
                        </td>
                      </tr>
                    ) : (
                      dispatch_items?.map((row: any, idx: number) => {
                        return (
                          <tr key={idx}>
                            <td style={tableStyles.cell}>{idx + 1}</td>

                            {/* Category */}
                            <td style={tableStyles.cell}>
                              <span>{row?.category?.category_name || "-"}</span>
                            </td>

                            {/* Sub Category */}
                            <td style={tableStyles.cell}>
                              <span>
                                {row?.sub_category?.sub_category_name || "-"}
                              </span>
                            </td>

                            {/* Item Name */}
                            <td style={tableStyles.cell}>
                              <span>{row?.item?.item_name || "-"}</span>
                            </td>

                            {/* Item Code */}
                            <td style={tableStyles.cell}>
                              <span>{row?.item_code || "-"}</span>
                            </td>

                            {/* Unit */}
                            <td style={tableStyles.cell}>
                              <span>
                                {row?.unit?.unit_name || row?.unit || "-"}
                              </span>
                            </td>

                            {/* Order Quantity */}
                            <td style={tableStyles.cell}>
                              <span>{row?.quantity || "-"}</span>
                            </td>

                            {/* Dispatch Quantity */}
                            <td style={tableStyles.cell}>
                              <span>
                                {row?.dispatch_quantity !== undefined &&
                                row.dispatch_quantity > 0
                                  ? row.dispatch_quantity
                                  : "-"}
                              </span>
                            </td>

                            {/* Rate */}
                            <td style={tableStyles.cell}>
                              <span>{row?.rate || "-"}</span>
                            </td>

                            {/* Amount */}
                            <td style={tableStyles.cell}>
                              <span>{row.amount || "-"}</span>
                            </td>

                            {/* Discount */}
                            <td style={tableStyles.cell}>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "8px",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <span>{row?.discount || "-"}</span>
                                <span>
                                  {row?.discount_type === "percentage"
                                    ? "%"
                                    : "₹"}
                                </span>
                              </div>
                            </td>

                            {/* Discount Amount */}
                            <td style={tableStyles.cell}>
                              <span>{row.discount_amount || "-"}</span>
                            </td>

                            {/* GST */}
                            <td style={tableStyles.cell}>
                              <span>{row?.gst?.gst_percent || "-"}</span>
                            </td>

                            {/* GST Amount */}
                            <td style={tableStyles.cell}>
                              <span>{row.gst_amount || "-"}</span>
                            </td>

                            {/* Total Amount */}
                            <td style={tableStyles.cell}>
                              <span>{row.total_amount || "-"}</span>
                            </td>

                            {/* Status - Scan Status */}
                            <td style={{ ...tableStyles.cell }}>
                              <OrderStatusBadge
                                status={row?.order_item_status || "-"}
                              />
                            </td>

                            <td style={tableStyles.cell}>
                              <TruncatedTextCell
                                text={row?.remark}
                                rowIndex={idx}
                                maxWidth="200px"
                                maxLines={2}
                              />
                            </td>

                            {/* Action (Bags) */}
                            <td
                              style={{
                                ...tableStyles.cell,
                                ...tableStyles.actionCell,
                              }}
                            >
                              <div style={{ textAlign: "center" }}>
                                {row?.dispatch_quantity &&
                                Number(row.dispatch_quantity) > 0 ? (
                                  <CloseButton
                                    onClick={() => {
                                      showConfirm(
                                        "Are you sure to cancel dispatch of this item?",
                                        "Confirm Cancellation",
                                        "Yes",
                                        "No"
                                      ).then((result) => {
                                        if (result.isConfirmed) {
                                          // Set all bags' status to null (Not Scanned)
                                          set_dispatch_items((prevItems) => {
                                            return prevItems.map(
                                              (item: any, itemIdx: number) => {
                                                if (
                                                  itemIdx === idx &&
                                                  item.bags &&
                                                  Array.isArray(item.bags)
                                                ) {
                                                  return {
                                                    ...item,
                                                    bags: item.bags.map(
                                                      (bag: any) => ({
                                                        ...bag,
                                                        status: null,
                                                      })
                                                    ),
                                                  };
                                                }
                                                return item;
                                              }
                                            );
                                          });
                                          // Clear verified bags for this item to set dispatch_quantity to 0
                                          setVerifiedItems((prev) => {
                                            const updated = { ...prev };
                                            delete updated[idx];
                                            return updated;
                                          });
                                        }
                                      });
                                    }}
                                  />
                                ) : row?.bags && row.bags.length > 0 ? (
                                  <ActionButton
                                    label="Scan"
                                    icon="bi bi-qr-code-scan"
                                    handleClick={() => {
                                      setViewBagsForItem(row.bags);
                                      setCurrentItemIndex(idx);
                                      setShow(true);
                                    }}
                                    variant="primary"
                                    disabled={
                                      row?.dispatch_quantity &&
                                      Number(row.dispatch_quantity) > 0
                                    }
                                  />
                                ) : (
                                  "-"
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
            <Row className="mb-4">
              <Col lg={7} className="mb-3">
                <div
                  style={{
                    border: `1px solid ${Colors?.background_color}`,
                    borderRadius: "4px",
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-2 mb-0 py-2 px-1"
                    style={{ background: Colors?.background_color }}
                  >
                    <i
                      className="bi bi-chevron-down"
                      style={{ fontSize: "0.675rem", color: "#000" }}
                    ></i>
                    <h6 className="mb-0">Remark</h6>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={7}
                    name="remark"
                    placeholder="Enter your remark"
                    value={formik.values.remark}
                    onChange={(e) =>
                      formik.setFieldValue("remark", e.target.value)
                    }
                    onBlur={formik.handleBlur}
                    style={{
                      border: "1px solid #transparant",
                      borderRadius: "0px",
                      resize: "vertical",
                    }}
                  />
                </div>
              </Col>
              <Col lg={5} className="mb-3">
                <div
                  style={{
                    backgroundColor: Colors?.background_color,
                    padding: "20px",
                    borderRadius: "8px",
                    border: `1px solid ${Colors?.background_color}`,
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                      Subtotal (Before Discount):
                    </span>
                    <span
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#212529",
                      }}
                    >
                      ₹{" "}
                      {Number(
                        dispatchFinancialSummary.subtotalBeforeDiscount
                      )?.toFixed(2)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                      Total Item Discounts:
                    </span>
                    <span
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#212529",
                      }}
                    >
                      ₹{" "}
                      {Number(
                        dispatchFinancialSummary.totalItemDiscounts
                      )?.toFixed(2)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                      Net Taxable Value:
                    </span>
                    <span
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#212529",
                      }}
                    >
                      ₹{" "}
                      {Number(
                        dispatchFinancialSummary.netTaxableValue
                      )?.toFixed(2)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                      Total GST:
                    </span>
                    <span
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#212529",
                      }}
                    >
                      ₹ {Number(dispatchFinancialSummary.totalGST)?.toFixed(2)}
                    </span>
                  </div>
                  <hr style={{ margin: "12px 0", borderColor: "#90caf9" }} />
                  <div className="d-flex justify-content-between align-items-center">
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "600",
                        color: "#212529",
                      }}
                    >
                      Total Amount:
                    </span>
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "700",
                        color: "#212529",
                      }}
                    >
                      ₹{" "}
                      {Number(dispatchFinancialSummary.totalAmount)?.toFixed(2)}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>

            <div className="d-flex justify-content-center gap-3 py-3">
              <CancelBackButton
                label="Cancel"
                onClick={() => {
                  showConfirm(
                    "Any unsaved changes will be lost.",
                    "Are you sure?",
                    "Yes",
                    "No"
                  ).then((result) => {
                    if (result.isConfirmed) {
                      navigate("/warehouse-dispatch");
                    }
                  });
                }}
              />

              <SubmitButton
                isSubmitting={formik.isSubmitting || grnLoading}
                labels={{
                  create: isEdit ? "Update Dispatch" : "Create Dispatch",
                  submitting: isEdit
                    ? "Updating Dispatch"
                    : "Creating Dispatch",
                }}
              />
            </div>
          </Form>
        )}
      </PageContainer>
      <ViewBagsModal
        show={show}
        handleClose={() => {
          setShow(false);
          setCurrentItemIndex(null);
        }}
        bags={viewBagsForItem}
        itemIndex={currentItemIndex}
        verifiedBags={
          currentItemIndex !== null ? verifiedItems[currentItemIndex] || [] : []
        }
        onBagsVerified={(
          itemIndex: number,
          verifiedBagIndices: number[],
          updatedBags: any[]
        ) => {
          setVerifiedItems((prev) => ({
            ...prev,
            [itemIndex]: verifiedBagIndices,
          }));

          // Update the bags array in dispatch_items with status
          set_dispatch_items((prevItems) => {
            return prevItems.map((item: any, idx: number) => {
              if (idx === itemIndex) {
                return {
                  ...item,
                  bags: updatedBags,
                };
              }
              return item;
            });
          });
        }}
      />
    </>
  );
};

export default WarehouseReadyForDispatchForm;
